import {
  categorySchema,
  createProductSchema,
  dashboardSchema,
  productSchema,
  monitoringHealthSchema,
  manualCheckResultSchema,
  type Category,
  type CreateProductInput,
  type Dashboard,
  type Product,
  type MonitoringHealth,
  type ManualCheckResult,
} from '@pricymayky/shared';
import { z } from 'zod';

class ApiError extends Error {
  constructor(message: string, public readonly status: number) { super(message); }
}

export const isPrivateMode = import.meta.env.VITE_APP_MODE === 'private';
const apiBase = isPrivateMode ? '/api/private' : '/api/demo';
let csrfToken: string | null = null;

async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const method = init?.method?.toUpperCase() ?? 'GET';
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(isPrivateMode && !['GET', 'HEAD'].includes(method) && csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...init?.headers,
    },
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const message = typeof body === 'object' && body !== null && 'error' in body ? String(body.error) : 'Wystąpił błąd połączenia.';
    throw new ApiError(message, response.status);
  }
  return schema.parse(body);
}

export const api = {
  dashboard: (): Promise<Dashboard> => request('/dashboard', dashboardSchema),
  products: (): Promise<Product[]> => request('/products', z.object({ products: z.array(productSchema), isDemo: z.boolean() })).then((data) => data.products),
  product: (id: string): Promise<Product> => request(`/products/${id}`, productSchema),
  categories: (): Promise<Category[]> => request('/categories', z.object({ categories: z.array(categorySchema), isDemo: z.boolean() })).then((data) => data.categories),
  createProduct: (input: CreateProductInput): Promise<Product> => request('/products', productSchema, { method: 'POST', body: JSON.stringify(createProductSchema.parse(input)) }),
  monitoringHealth: (): Promise<MonitoringHealth> => request('/monitoring/health', monitoringHealthSchema),
  checkOffer: (offerId: string): Promise<ManualCheckResult> => request(`/offers/${offerId}/check`, manualCheckResultSchema, { method: 'POST' }),
  session: async (): Promise<{ authenticated: true; email: string }> => {
    const response = await fetch('/api/auth/session', { credentials: 'same-origin' });
    if (!response.ok) throw new ApiError('Wymagane logowanie.', response.status);
    const session = z.object({ authenticated: z.literal(true), email: z.string().email(), csrfToken: z.string().min(20) }).parse(await response.json());
    csrfToken = session.csrfToken;
    return session;
  },
  login: async (email: string, password: string): Promise<{ email: string }> => {
    const response = await fetch('/api/auth/login', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
    const body: unknown = await response.json();
    if (!response.ok) throw new ApiError(typeof body === 'object' && body !== null && 'error' in body ? String(body.error) : 'Nie udało się zalogować.', response.status);
    const login = z.object({ email: z.string().email(), csrfToken: z.string().min(20) }).parse(body);
    csrfToken = login.csrfToken;
    return login;
  },
};
